import { FC, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { Link as RouterLink } from "react-router-dom";
import { AdminRoutes } from "config/routes";
import { useAuthUser } from "hooks/useAuthUser";
import { useGetOrganizationByUserIdQuery } from "graphql/__generated__/hooks";
import { ROLES } from "types/user";
import { getNonTestOrganizationIds } from "utils/testOrganization";
import {
  DEFAULT_METRICS_DATE_RANGE,
  getMetricsDateRangeLabel,
  MetricsDateRange,
} from "../metricsDateRange";
import {
  formatHospitalLabel,
  HospitalCompetitionRank,
} from "../hospitalCompetitionRanking";
import { useAllDomainsFeedbackCompetition } from "../useAllDomainsFeedbackCompetition";

type Props = {
  /** Defaults to the selected organization in the app shell. */
  orgId?: string | null;
  dateRange?: MetricsDateRange;
  /** Show every hospital instead of the 10-row card preview. */
  showAll?: boolean;
};

function competitionTitle(domainName: string | null): string {
  if (!domainName) {
    return "Hospital competition";
  }
  const spaced = domainName.replace(/([a-z])([A-Z])/g, "$1 $2").trim();
  return `${spaced} wide competition`;
}

const PLACE_TROPHY_COLOR: Record<number, string> = {
  1: "#C9A227",
  2: "#8E99A4",
  3: "#B87333",
};

const PlaceTrophy: FC<{ rank: number; tied: boolean }> = ({ rank, tied }) => {
  const color = tied ? undefined : PLACE_TROPHY_COLOR[rank];
  return (
    <Box
      sx={{
        width: 22,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {color && (
        <EmojiEventsIcon
          sx={{ color, fontSize: 22 }}
          aria-label={
            rank === 1 ? "First place" : rank === 2 ? "Second place" : "Third place"
          }
        />
      )}
    </Box>
  );
};

type CardProps = {
  title: string;
  periodLabel: string;
  ranks: HospitalCompetitionRank[];
  viewerOrgIds?: string[];
  loading?: boolean;
  error?: boolean;
  emptyMessage?: string | null;
  showAll?: boolean;
};

const CompetitionCard: FC<CardProps> = ({
  title,
  periodLabel,
  ranks,
  viewerOrgIds = [],
  loading = false,
  error = false,
  emptyMessage = null,
  showAll = false,
}) => {
  const visibleRanks = showAll ? ranks : ranks.slice(0, 10);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <EmojiEventsOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="h6" sx={{ mb: 0 }}>
            {title}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Hospitals ranked by how many of their people submitted feedback in the{" "}
          {periodLabel}. Only relative place is shown — not the percentages.
        </Typography>

        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Alert severity="error">Could not load the hospital ranking.</Alert>
        )}

        {!loading && !error && emptyMessage && (
          <Alert severity="info">{emptyMessage}</Alert>
        )}

        {!loading && !error && ranks.length > 0 && (
          <Stack spacing={1}>
            {visibleRanks.map((hospital) => {
              const isViewer = viewerOrgIds.includes(hospital.orgId);
              return (
                <Box
                  key={hospital.orgId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1,
                    bgcolor: isViewer ? "action.selected" : "transparent",
                    border: "1px solid",
                    borderColor: isViewer ? "primary.light" : "transparent",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <PlaceTrophy rank={hospital.rank} tied={hospital.tied} />
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ minWidth: 36, fontVariantNumeric: "tabular-nums" }}
                    >
                      #{hospital.rank}
                      {hospital.tied ? "*" : ""}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={isViewer ? 600 : 400}
                    >
                      {formatHospitalLabel(hospital.orgName)}
                    </Typography>
                  </Stack>
                  {isViewer && (
                    <Chip
                      label="Your hospital"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              );
            })}
            {visibleRanks.some((row) => row.tied) && (
              <Typography variant="caption" color="text.secondary">
                * Tied with another hospital
              </Typography>
            )}
            {!showAll && ranks.length > 10 && (
              <Button
                component={RouterLink}
                to={AdminRoutes.HospitalCompetition}
                size="small"
                sx={{ alignSelf: "flex-start" }}
              >
                View all rankings
              </Button>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

/** Rankings for every domain reached through any organization the user has. */
const UserDomainsCompetition: FC<Props> = ({
  orgId: orgIdProp,
  dateRange = DEFAULT_METRICS_DATE_RANGE,
  showAll = false,
}) => {
  const { user } = useAuthUser();

  const { data: userOrgsData, loading: userOrgsLoading } =
    useGetOrganizationByUserIdQuery({
      variables: { userId: user?.user_id || "" },
      skip: !user?.user_id,
    });

  const viewerOrgIds = useMemo(
    () => [
      ...new Set([
        ...(orgIdProp ? [orgIdProp] : []),
        ...(user?.org_id ? [user.org_id] : []),
        ...getNonTestOrganizationIds(userOrgsData?.organization || []),
      ]),
    ],
    [orgIdProp, user?.org_id, userOrgsData],
  );

  const { domains, loading, error } = useAllDomainsFeedbackCompetition(
    dateRange,
    viewerOrgIds,
  );
  const periodLabel = getMetricsDateRangeLabel(dateRange).toLowerCase();

  if (userOrgsLoading || loading || error) {
    return (
      <CompetitionCard
        title="Hospital competition"
        periodLabel={periodLabel}
        ranks={[]}
        loading={userOrgsLoading || loading}
        error={error}
      />
    );
  }

  if (domains.length === 0) return null;

  return (
    <Stack spacing={2}>
      {domains.map((domain) => (
        <CompetitionCard
          key={domain.domainId}
          title={competitionTitle(domain.domainName)}
          periodLabel={periodLabel}
          ranks={domain.ranks}
          viewerOrgIds={viewerOrgIds}
          showAll={showAll}
        />
      ))}
    </Stack>
  );
};

/**
 * Admins configure domains, so instead of hiding the widget they get told what
 * is missing.
 */
const ADMIN_EMPTY_MESSAGES = {
  "no-domains":
    "No organization domains exist yet. Group sibling hospitals into a domain from the Organizations page to start a competition.",
  "single-hospital-domains":
    "Every domain has a single hospital. Add at least two hospitals to a domain so they can be ranked against each other.",
  "no-learner-data":
    "No hospital in these domains has learners yet, so there is nothing to rank.",
  "no-org":
    "Your profile is not linked to an organization, so there is no hospital competition to show.",
  "no-domain":
    "Your organization is not in a domain with peer hospitals yet.",
} as const;

/** Every domain's ranking, for admins who oversee all hospitals. */
const AllDomainsCompetition: FC<Props> = ({
  dateRange = DEFAULT_METRICS_DATE_RANGE,
  showAll = false,
}) => {
  const { domains, loading, error, emptyReason } =
    useAllDomainsFeedbackCompetition(dateRange);
  const periodLabel = getMetricsDateRangeLabel(dateRange).toLowerCase();

  if (loading || error || emptyReason) {
    return (
      <CompetitionCard
        title="Hospital competition"
        periodLabel={periodLabel}
        ranks={[]}
        loading={loading}
        error={error}
        emptyMessage={
          emptyReason && emptyReason in ADMIN_EMPTY_MESSAGES
            ? ADMIN_EMPTY_MESSAGES[
                emptyReason as keyof typeof ADMIN_EMPTY_MESSAGES
              ]
            : null
        }
      />
    );
  }

  return (
    <Stack spacing={2}>
      {domains.map((domain) => (
        <CompetitionCard
          key={domain.domainId}
          title={competitionTitle(domain.domainName)}
          periodLabel={periodLabel}
          ranks={domain.ranks}
          showAll={showAll}
        />
      ))}
    </Stack>
  );
};

export const HospitalFeedbackCompetitionWidget: FC<Props> = (props) => {
  const { role } = useAuthUser();

  return role === ROLES.ADMIN ? (
    <AllDomainsCompetition {...props} />
  ) : (
    <UserDomainsCompetition {...props} />
  );
};
